import './xeroDeliveryDetailsSection.scss';

import { AutoDropdown, Field, FocusTrap, Popup, TextAreaEditor, TextEditor, TransparentButton } from '@approvalmax/ui';
import { stringHelpers } from '@approvalmax/utils';
import { constants, selectors } from 'modules/common';
import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { DropdownTriangleIcon } from 'modules/sprites';
import { FC, memo } from 'react';
import bemFactory from 'react-bem-factory';
import { useUserBetaFeatures } from 'shared/hooks';

import {
    cancelActivePopup,
    changeXeroDeliveryDetails,
    selectXeroDeliveryAddress,
    showXeroContactAddressSearchPopup,
} from '../../../actions';
import { XeroAddress } from '../../../data/xero/XeroAddress';
import { XERO_CONTACT_ADDRESS_SEARCH_POPUP } from '../../../reducers/page/activePopup/xeroContactAddressSearchPopupReducer';
import { getActivePopupId, getShowValidationErrors } from '../../../selectors/pageSelectors';
import { getFieldsApprovalPermissionBySystemPurpose } from '../../../selectors/requestSelectors';
import { getXeroAddressTypeText, getXeroContext } from '../../../selectors/xero';
import { XeroContactAddressSearchPopup } from '../XeroContactAddressSearchPopup/XeroContactAddressSearchPopup';
import XeroContactAddressSearchPopupOld from '../XeroContactAddressSearchPopupOld/XeroContactAddressSearchPopup';
import { messages } from './XeroDeliveryDetailsSection.messages';

const { xeroConstants } = constants;

const bem = bemFactory.block('reqf-xero-delivery-details-section');
const qa = bemFactory.qa('reqf-xero-delivery-details-section');

interface Props {
    request: domain.XeroPoRequest;
    className?: string;
}

/**
 * @deprecated use redesigned {@link XeroDeliveryDetailsSection}
 */
export const XeroDeliveryDetailsSection: FC<Props> = memo((props) => {
    const { className, request } = props;

    const dispatch = useDispatch();
    const showErrors = useSelector(getShowValidationErrors);
    const xeroContext = useSelector(getXeroContext);
    const addresses = xeroContext?.companyAddresses;
    const activePopupId = useSelector(getActivePopupId);
    const instructions = request.details.delivery.instructions;
    const [deliverFieldPermission] = useSelector((state) =>
        getFieldsApprovalPermissionBySystemPurpose(state, request, [domain.FieldSystemPurpose.XeroDeliveryAddress])
    );
    const { isBelgradeUIXeroContactAddressSearchPopup } = useUserBetaFeatures();

    const checkIsFieldInvalid = (value: string, maxLength: number) => {
        if (value.length === 0) {
            return false;
        }

        if (!value.trim()) {
            return true;
        }

        if (value.length > maxLength) {
            return true;
        }

        return false;
    };

    const handleChangeAddress = (value: string) => {
        dispatch(
            changeXeroDeliveryDetails({
                ...request.details.delivery,
                address: stringHelpers.escapeSpecificExpressions(value, xeroConstants.XERO_NOT_ALLOWED_SYMBOLS),
            })
        );
    };

    const handleChangeAttentionTo = (value: string) => {
        dispatch(
            changeXeroDeliveryDetails({
                ...request.details.delivery,
                attentionTo: stringHelpers.escapeSpecificExpressions(value, xeroConstants.XERO_NOT_ALLOWED_SYMBOLS),
            })
        );
    };

    const handleChangePhone = (value: string) => {
        dispatch(changeXeroDeliveryDetails({ ...request.details.delivery, phone: value }));
    };

    const handleChangeInstructions = (value: string) => {
        dispatch(
            changeXeroDeliveryDetails({
                ...request.details.delivery,
                instructions: stringHelpers.escapeSpecificExpressions(value, xeroConstants.XERO_NOT_ALLOWED_SYMBOLS),
            })
        );
    };

    const selectAddress = (address: XeroAddress) => {
        dispatch(selectXeroDeliveryAddress(address));
    };

    const handleShowXeroContactAddressSearchPopup = () => {
        dispatch(showXeroContactAddressSearchPopup());
    };

    const handleCancelActivePopup = () => {
        dispatch(cancelActivePopup());
    };

    const deliveryDetails = request.details.delivery;

    const isInvalidDeliveryAddress =
        checkIsFieldInvalid(deliveryDetails.address, xeroConstants.DELIVERY_ADDRESS_MAX_LENGTH) ||
        (deliverFieldPermission.required && !deliveryDetails.address);

    const isInvalidDeliveryAttentionTo = checkIsFieldInvalid(
        deliveryDetails.attentionTo,
        xeroConstants.DELIVERY_ATTENTION_TO_MAX_LENGTH
    );

    const isInvalidDeliveryPhone =
        !selectors.ui.isValidPhone(deliveryDetails.phone) ||
        checkIsFieldInvalid(deliveryDetails.phone, xeroConstants.DELIVERY_PHONE_MAX_LENGTH);

    const isInvalidDeliveryInstructions = checkIsFieldInvalid(
        instructions,
        xeroConstants.DELIVERY_INSTRUCTIONS_MAX_LENGTH
    );

    const instructionMaxLength = xeroConstants.DELIVERY_INSTRUCTIONS_MAX_LENGTH;

    return (
        <div className={bem.add(className)()} data-qa={qa()}>
            <div>
                <AutoDropdown
                    className={bem('header')}
                    button={
                        <TransparentButton>
                            {({ elementProps }) => (
                                <button
                                    {...elementProps}
                                    className={bem('header-button', { disabled: deliverFieldPermission.disabled })}
                                    data-qa={qa('header-button')}
                                    disabled={deliverFieldPermission.disabled}
                                >
                                    <div className={bem('header-text')}>{messages.deliveryAddressHeader}</div>

                                    {!deliverFieldPermission.disabled && (
                                        <DropdownTriangleIcon className={bem('header-icon')} width={10} height={5} />
                                    )}
                                </button>
                            )}
                        </TransparentButton>
                    }
                >
                    <div className={bem('dropdown-panel')} data-qa={qa('dropdown-panel')}>
                        <div
                            className={bem('dropdown-search-button')}
                            data-qa={qa('dropdown-search-button')}
                            onMouseDown={handleShowXeroContactAddressSearchPopup}
                        >
                            {messages.searchFromContactsButton}
                        </div>

                        {addresses.map((address) => {
                            return (
                                <div
                                    data-qa={qa('dropdown-address-item')}
                                    key={address.id}
                                    className={bem('dropdown-address-item')}
                                    title={address.text}
                                    onMouseDown={() => selectAddress(address)}
                                >
                                    <div className={bem('dropdown-address-type')}>
                                        {getXeroAddressTypeText(address)}
                                    </div>

                                    <div>{address.text}</div>

                                    {address.attentionTo && <div>{address.attentionTo}</div>}
                                </div>
                            );
                        })}
                    </div>
                </AutoDropdown>
            </div>

            <div className={bem('content')}>
                <Field
                    className={bem('address-column')}
                    title={messages.addressTitle}
                    required={deliverFieldPermission.required}
                >
                    <TextAreaEditor
                        qa={qa('address-editor')}
                        invalid={showErrors && isInvalidDeliveryAddress}
                        maxLength={xeroConstants.DELIVERY_ADDRESS_MAX_LENGTH}
                        value={deliveryDetails.address}
                        minHeight={4}
                        maxHeight={12}
                        onChange={handleChangeAddress}
                        placeholder={messages.addressPlaceholder}
                        disabled={deliverFieldPermission.disabled}
                    />
                </Field>

                <div className={bem('mid-column')}>
                    <Field title={messages.attentionTitle}>
                        <TextEditor
                            qa={qa('attention-to-editor')}
                            invalid={showErrors && isInvalidDeliveryAttentionTo}
                            maxLength={xeroConstants.DELIVERY_ATTENTION_TO_MAX_LENGTH}
                            value={deliveryDetails.attentionTo}
                            onChange={handleChangeAttentionTo}
                            placeholder={messages.attentionPlaceholder}
                            disabled={deliverFieldPermission.disabled}
                        />
                    </Field>

                    <Field title={messages.phoneTitle}>
                        <TextEditor
                            qa={qa('delivery-details-editor')}
                            invalid={showErrors && isInvalidDeliveryPhone}
                            maxLength={xeroConstants.DELIVERY_PHONE_MAX_LENGTH}
                            value={deliveryDetails.phone}
                            onChange={handleChangePhone}
                            placeholder={messages.phonePlaceholder}
                            changeOnBlur
                            disabled={deliverFieldPermission.disabled}
                        />
                    </Field>
                </div>

                <Field className={bem('instructions-column')} title={messages.deliveryInstructionsTitle}>
                    <FocusTrap>
                        {({ focused }) => (
                            <div className={bem('instructions-content')}>
                                <TextAreaEditor
                                    qa={qa('instructions-editor')}
                                    value={instructions}
                                    minHeight={4}
                                    maxHeight={12}
                                    maxLength={xeroConstants.DELIVERY_INSTRUCTIONS_MAX_LENGTH}
                                    invalid={showErrors && isInvalidDeliveryInstructions}
                                    onChange={handleChangeInstructions}
                                    placeholder={messages.deliveryInstructionsPlaceholder}
                                    disabled={deliverFieldPermission.disabled}
                                />

                                <div className={bem('instructions-counter', { active: focused })}>
                                    {`${instructionMaxLength - instructions.length} / ${instructionMaxLength}`}
                                </div>
                            </div>
                        )}
                    </FocusTrap>
                </Field>
            </div>

            {isBelgradeUIXeroContactAddressSearchPopup ? (
                <XeroContactAddressSearchPopup
                    open={activePopupId === XERO_CONTACT_ADDRESS_SEARCH_POPUP}
                    onClose={handleCancelActivePopup}
                />
            ) : (
                <Popup
                    isOpen={activePopupId === XERO_CONTACT_ADDRESS_SEARCH_POPUP}
                    onRequestClose={handleCancelActivePopup}
                >
                    <XeroContactAddressSearchPopupOld />
                </Popup>
            )}
        </div>
    );
});

XeroDeliveryDetailsSection.displayName = 'XeroDeliveryDetailsSection';
